import { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, TextField, Button, Typography, Link, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { trpc } from '../../lib/trpc';

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Forgot password page component
 * Handles password reset request with API integration
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const requestResetMutation = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (err) => {
      setError(err.message || 'Failed to send reset email. Please try again.');
    },
  });

  /**
   * Validate email field
   */
  const validateEmail = useCallback((): string | null => {
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!isValidEmail(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  }, [email]);

  /**
   * Handle email blur for validation
   */
  const handleBlur = () => {
    setTouched(true);
    const error = validateEmail();
    setEmailError(error);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTouched(true);

    // Validate email
    const validationError = validateEmail();
    setEmailError(validationError);

    if (validationError) {
      return;
    }

    // Submit password reset request
    requestResetMutation.mutate({ email: email.trim().toLowerCase() });
  };

  /**
   * Handle try again action
   */
  const handleTryAgain = () => {
    setSubmitted(false);
    setEmail('');
    setError(null);
    setEmailError(null);
    setTouched(false);
  };

  const isLoading = requestResetMutation.isPending;

  // Success state - show confirmation message
  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Check your email
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          We&apos;ve sent password reset instructions to{' '}
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {email}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Didn&apos;t receive the email? Check your spam folder or try again with a different email
          address.
        </Typography>
        <Button
          variant="outlined"
          onClick={handleTryAgain}
          sx={{ mb: 2 }}
          aria-label="Try with another email"
        >
          Try another email
        </Button>
        <Box>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowBackIcon fontSize="small" aria-hidden="true" />
            Back to login
          </Link>
        </Box>
      </Box>
    );
  }

  // Form state - show email input
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Reset your password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your email address and we&apos;ll send you instructions to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="email"
        name="email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={handleBlur}
        error={touched && !!emailError}
        helperText={touched && emailError}
        required
        autoComplete="email"
        autoFocus
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Email address',
          'aria-describedby': emailError ? 'email-error' : undefined,
        }}
        sx={{ mb: 3 }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        aria-label={isLoading ? 'Sending reset instructions' : 'Send reset instructions'}
        sx={{ mb: 2, height: 48 }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <span>Sending...</span>
          </Box>
        ) : (
          'Send reset instructions'
        )}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to="/login"
          sx={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
          tabIndex={isLoading ? -1 : 0}
        >
          <ArrowBackIcon fontSize="small" aria-hidden="true" />
          Back to login
        </Link>
      </Box>
    </Box>
  );
}

export default ForgotPasswordPage;
